import React, { useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from '@tasama/i18n';
import { DocumentUploadSection } from '@tasama/ui';
import { useFileUploadProgress } from '@tasama/utils';
import type { FormFieldSchema } from '@tasama/types';
import { stripFieldLabelHtml } from './strip-field-label-html.util';
import {
  ACTIVITY_REQUEST_ACCEPT,
  getValidateActivityRequestFiles,
} from '../../schemas/activity-request.schema';

// Each dynamic 'file' field is its own independent upload slot (one per ServiceNow
// variable), so the request-wide "10 files" cap does not apply per field — a form with
// N file fields would otherwise allow N x 10 files. Every dynamic field accepts 1 file.
const MAX_FILES = 1;

interface DynamicFileEntry {
  id: string;
  fileName: string;
  fileSize: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  file: File;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

interface DynamicFileUploadFieldProps {
  field: FormFieldSchema;
  control: Control<Record<string, unknown>>;
  testID: string;
}

// Local file-list state syncs into RHF via Controller (field value = File[]) — mirrors the
// static required-documents upload in ActivityRequestFormContainer, but scoped per dynamic
// field since each 'file'-type field needs its own independent list.
//
// Format and size come from the one Activity Request attachment contract (TDF-1056 §4,
// BRD §4.3.2): PDF, PNG, CSV, Excel, Word, JPG, 5 MB per file. The "10 files" part of that
// contract describes the request as a whole (the static supporting-documents dropzone);
// it does not apply per dynamic field, so this field caps at 1 (see MAX_FILES above).
export function DynamicFileUploadField({
  field,
  control,
  testID,
}: DynamicFileUploadFieldProps) {
  const { t } = useTranslation('portal');
  const [entries, setEntries] = useState<DynamicFileEntry[]>([]);
  const [isFileRemoved, setIsFileRemoved] = useState(false);
  const [fileError, setFileError] = useState<string | undefined>();
  const entriesRef = React.useRef<DynamicFileEntry[]>(entries);
  entriesRef.current = entries;
  const { startProgress } = useFileUploadProgress();

  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: rhf }) => {
        const handleFilesSelected = (files: FileList) => {
          setIsFileRemoved(false);
          const fileArray = Array.from(files);

          // Reject on format / size before anything is added to the list, so a bad
          // file never shows a progress card it will not be allowed to keep.
          const candidates = fileArray.map((file, index) => ({
            id: `${field.name}-file-${Date.now()}-${index}`,
            fileName: file.name,
            fileSize: formatFileSize(file.size),
            bytes: file.size,
            progress: 0,
            status: 'pending' as const,
          }));
          const result = getValidateActivityRequestFiles(t)({
            uploadedFiles: candidates,
          });
          if (!result.success) {
            setFileError(
              result.errors?.uploadedFiles ??
                Object.values(result.errors ?? {})[0]
            );
            return;
          }
          setFileError(undefined);

          const newEntries: DynamicFileEntry[] = fileArray.map(
            (file, index) => ({
              id: candidates[index]!.id,
              fileName: file.name,
              fileSize: formatFileSize(file.size),
              progress: 0,
              status: 'uploading' as const,
              file,
            })
          );

          const updated = [...entriesRef.current, ...newEntries];
          setEntries(updated);
          rhf.onChange(updated.map((e) => e.file));

          newEntries.forEach((entry, index) => {
            startProgress({
              fileId: entry.id,
              delayMs: index * 100,
              getLatestProgress: () =>
                entriesRef.current.find((e) => e.id === entry.id)?.progress,
              onUpdate: (progress, status) => {
                setEntries((prev) =>
                  prev.map((e) =>
                    e.id === entry.id ? { ...e, progress, status } : e
                  )
                );
              },
            });
          });
        };

        const handleFileRemove = (fileId: string) => {
          setIsFileRemoved(true);
          setFileError(undefined);
          const updated = entriesRef.current.filter((e) => e.id !== fileId);
          setEntries(updated);
          rhf.onChange(updated.map((e) => e.file));
        };

        return (
          <DocumentUploadSection
            label={stripFieldLabelHtml(field.label)}
            isCompleted={entries.length > 0}
            files={entries}
            onFilesSelected={handleFilesSelected}
            onFileRemove={handleFileRemove}
            isFileRemoved={isFileRemoved}
            accept={ACTIVITY_REQUEST_ACCEPT}
            multiple
            maxFiles={MAX_FILES}
            onMaxFilesExceeded={() =>
              setFileError(
                t('services.activityRequest.validation.tooManyFiles')
              )
            }
            supportingText={t(
              'services.activityRequest.fields.fileSupportingText'
            )}
            filesPosition="above"
            errorMessage={fileError}
            testID={testID}
          />
        );
      }}
    />
  );
}
